/**
 * sl-amfa.7.1 + 7.2: PublishWizard
 *
 * Multi-step wizard for publishing automation templates to the marketplace.
 * Steps: 1) Preview, 2) Metadata, 3) Credential Review, 4) Consent + Submit
 *
 * SOLID:
 * - S: Wizard shell with step routing, delegates step rendering to sub-components
 */
import { Modal } from '@affine/component/ui/modal';
import { useLocalePicker } from '@affine/core/hooks/use-locale-picker';
import { workspaceApiFetch } from '@affine/core/modules/cloud';
import { useFeatureGate } from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { useI18n } from '@affine/i18n';
import clsx from 'clsx';
import { useCallback, useEffect, useReducer, useState } from 'react';

import { CustomSelect } from './custom-select';
import type { ParameterizedValue } from './extracted-vars-editor';
import { I18nFieldEditor } from './i18n-field-editor';
import { PrePublishReview } from './pre-publish-review';
import {
  canNavigateToPublishStep,
  type PublishReviewStep,
} from './publish-navigation';
import type { TemplateRightsMetadata } from './publish-request';
import {
  buildPublishRequest,
  buildTechnicalRevisionRequest,
} from './publish-request';
import {
  createWizardFieldTranslate,
  getPrimaryPublishDescription,
  isPublishDescriptionComplete,
  MIN_PUBLISH_DESCRIPTION_LENGTH,
  PublishConsentSection,
  PublishDescriptionEditor,
  PublishPreviewImageEditor,
  PublishVisibilitySelector,
  resolvePublishEnforcedLocale,
  resolvePublishFieldAnchor,
} from './publish-shared';
import type {
  PublishWizardSource,
  TemplatePublishSourceKind,
} from './publish-source';
import { getTemplatePublishSourceId } from './publish-source';
import {
  SlotDescriptionEditor,
  type SlotDescriptionsState,
  type SlotRef,
} from './slot-description-editor';
import * as styles from './styles.css';
import type { ParameterizedTriggerOverride } from './trigger-review-step';
import { TriggerReviewStep } from './trigger-review-step';

// =============================================================================
// Types
// =============================================================================

export interface PublishMetadata {
  // sl-k94y.1.1: BCP-47 keyed title map. The editor renders whatever the
  // deployment exposes via LocaleSettings.availableLocales; do not assume
  // a fixed de/en set here.
  titlesI18n: Record<string, string>;
  description: string;
  descriptionsI18n?: Record<string, string>;
  category: string;
  tags: string[];
  previewImageBlobId?: string;
  // sl-kypm: 3-Level marketplace fields
  templateType: TemplatePublishSourceKind;
  stepInsertType?: string;
}

export interface DependentDoc {
  id: string;
  title: string;
  type: string;
}

export interface CredentialRequirement {
  id: string;
  type: string;
  displayName: string;
  isRequired: boolean;
  isAiGateway: boolean;
}

interface ApiCredentialRequirement {
  type: string;
  label: string;
  required: boolean;
  isProvidedByStreamlain: boolean;
}

type PublishStep = 'packaging' | 'analyzing' | 'uploading' | 'done' | 'error';

interface PublishResult {
  status: 'PENDING_REVIEW' | 'APPROVED';
  templateId: string;
  versionNumber: number;
  semanticVersion: string;
  findings?: Array<{ step: string; reason: string }>;
}

/** sl-u319.1.2: Visibility scope for published template */
export type PublishVisibility = 'workspace' | 'global';

interface WizardState {
  step: PublishReviewStep;
  docIds: string[];
  dependentDocs: DependentDoc[];
  metadata: PublishMetadata;
  credentials: CredentialRequirement[];
  previewAcknowledged: boolean;
  /** sl-u319.1.2: Publish scope — workspace-only or global marketplace */
  visibility: PublishVisibility;
  /**
   * sl-mkx.4.2: per-slot, per-locale descriptions. Only populated for
   * kind='step'; ignored for workflow/automation publishes.
   */
  slotDescriptions: SlotDescriptionsState;
  /**
   * sl-mkx.4.2: release notes for version-updates. Required when
   * existingTemplateId is set (re-publish), empty string on first publish.
   */
  changelog: string;
  consentGdpr: boolean;
  consentLicense: boolean;
  rightsMetadata: TemplateRightsMetadata;
  isSubmitting: boolean;
  publishStep: PublishStep | null;
  submitError: string | null;
  submitFindings: Array<{ step: string; reason: string }>;
  publishResult: PublishResult | null;
  technicalRevision: {
    rootArtifactRevisionId: string;
    contractHash: string;
    reused: boolean;
    automationCount: number;
    maxAutomations: number;
    revisions: Array<{
      slotKey: string;
      label: string;
      disposition: 'reused' | 'new' | 'derived';
      inputs: string[];
      outputs: string[];
    }>;
    credentialRequirements: string[];
    endpoints: Array<{
      url: string;
      classification: 'public' | 'parameterized' | 'internal';
    }>;
    sharedResources: string[];
    findings: Array<{
      severity: 'info' | 'warning' | 'error';
      message: string;
    }>;
    canSaveTechnicalRevision: boolean;
    canPromoteListing: boolean;
  } | null;
  isSavingTechnicalRevision: boolean;
  technicalRevisionError: string | null;
}

function normalizeCredentialRequirements(
  raw: unknown
): CredentialRequirement[] {
  const requirements = Array.isArray(raw)
    ? raw
    : typeof raw === 'object' && raw !== null
      ? (raw as { credentialRequirements?: unknown }).credentialRequirements
      : [];
  if (!Array.isArray(requirements)) return [];

  return requirements
    .filter(
      (item): item is ApiCredentialRequirement =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as ApiCredentialRequirement).type === 'string' &&
        typeof (item as ApiCredentialRequirement).label === 'string'
    )
    .map(item => ({
      id: item.type,
      type: item.type,
      displayName: item.label,
      isRequired: item.required,
      isAiGateway: item.isProvidedByStreamlain,
    }));
}

type WizardAction =
  | { type: 'SET_STEP'; step: PublishReviewStep }
  | { type: 'SET_DEPENDENT_DOCS'; docs: DependentDoc[] }
  | { type: 'SET_METADATA'; metadata: Partial<PublishMetadata> }
  | { type: 'SET_CREDENTIALS'; credentials: CredentialRequirement[] }
  | {
      type: 'UPDATE_CREDENTIAL';
      id: string;
      changes: Partial<CredentialRequirement>;
    }
  | { type: 'SET_PREVIEW_ACK'; value: boolean }
  | { type: 'SET_VISIBILITY'; value: PublishVisibility }
  | { type: 'SET_SLOT_DESCRIPTIONS'; value: SlotDescriptionsState }
  | { type: 'SET_CHANGELOG'; value: string }
  | { type: 'SET_CONSENT_GDPR'; value: boolean }
  | { type: 'SET_CONSENT_LICENSE'; value: boolean }
  | { type: 'SET_RIGHTS_METADATA'; value: Partial<TemplateRightsMetadata> }
  | { type: 'SET_SUBMITTING'; value: boolean }
  | { type: 'SET_PUBLISH_STEP'; step: PublishStep | null }
  | {
      type: 'SET_SUBMIT_ERROR';
      error: string | null;
      findings?: Array<{ step: string; reason: string }>;
    }
  | { type: 'SET_PUBLISH_RESULT'; result: PublishResult | null }
  | {
      type: 'SET_TECHNICAL_REVISION';
      result: WizardState['technicalRevision'];
    }
  | { type: 'SET_TECHNICAL_REVISION_SAVING'; value: boolean }
  | { type: 'SET_TECHNICAL_REVISION_ERROR'; error: string | null };

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.step };
    case 'SET_DEPENDENT_DOCS':
      return { ...state, dependentDocs: action.docs };
    case 'SET_METADATA':
      return { ...state, metadata: { ...state.metadata, ...action.metadata } };
    case 'SET_CREDENTIALS':
      return { ...state, credentials: action.credentials };
    case 'UPDATE_CREDENTIAL':
      return {
        ...state,
        credentials: state.credentials.map(c =>
          c.id === action.id ? { ...c, ...action.changes } : c
        ),
      };
    case 'SET_PREVIEW_ACK':
      return { ...state, previewAcknowledged: action.value };
    case 'SET_VISIBILITY':
      return { ...state, visibility: action.value };
    case 'SET_SLOT_DESCRIPTIONS':
      return { ...state, slotDescriptions: action.value };
    case 'SET_CHANGELOG':
      return { ...state, changelog: action.value };
    case 'SET_CONSENT_GDPR':
      return { ...state, consentGdpr: action.value };
    case 'SET_CONSENT_LICENSE':
      return { ...state, consentLicense: action.value };
    case 'SET_RIGHTS_METADATA':
      return {
        ...state,
        rightsMetadata: { ...state.rightsMetadata, ...action.value },
      };
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.value };
    case 'SET_PUBLISH_STEP':
      return { ...state, publishStep: action.step };
    case 'SET_SUBMIT_ERROR':
      return {
        ...state,
        submitError: action.error,
        submitFindings: action.findings ?? [],
      };
    case 'SET_PUBLISH_RESULT':
      return { ...state, publishResult: action.result };
    case 'SET_TECHNICAL_REVISION':
      return { ...state, technicalRevision: action.result };
    case 'SET_TECHNICAL_REVISION_SAVING':
      return { ...state, isSavingTechnicalRevision: action.value };
    case 'SET_TECHNICAL_REVISION_ERROR':
      return { ...state, technicalRevisionError: action.error };
  }
}

const CATEGORIES = [
  'Accounting',
  'Marketing',
  'HR',
  'Data Processing',
  'AI / ML',
  'Custom',
];

// sl-vgk8.4: Node insert types for node marketplace
const REVIEW_STEP_KEYS = [
  'com.streamlain.marketplace.publish.step.scope',
  'com.streamlain.marketplace.publish.step.details',
  'com.streamlain.marketplace.publish.step.safety',
  'com.streamlain.marketplace.publish.step.release',
] as const;

function isStandaloneAiNodePublish(input: PublishWizardInput): boolean {
  return input.kind === 'step' && input.isAiStep === true;
}

/**
 * sl-k94y.7.2: walks step-kind slot arrays and reports any required slot
 * whose `descriptionsI18n[enforcedLocale]` is empty. Mirrors the backend
 * `iterateSlots` predicate (`slot.required === true`) so the wizard's
 * pre-submit gate matches the cross-deployment baseline-walk that
 * `MarketplaceValidationService.validatePublishedSlots` runs.
 */
function collectMissingSlotBaselines(
  inputSlots: readonly SlotRef[],
  configSlots: readonly SlotRef[],
  outputSlots: readonly SlotRef[],
  slotDescriptions: SlotDescriptionsState,
  enforcedLocale: string
): Array<{
  group: 'inputSlots' | 'configSlots' | 'outputSlots';
  slotId: string;
  slotName: string;
}> {
  const out: Array<{
    group: 'inputSlots' | 'configSlots' | 'outputSlots';
    slotId: string;
    slotName: string;
  }> = [];
  const groups = [
    ['inputSlots', inputSlots],
    ['configSlots', configSlots],
    ['outputSlots', outputSlots],
  ] as const;
  for (const [group, slots] of groups) {
    for (const slot of slots) {
      if (!slot.required) continue;
      const value = slotDescriptions[slot.id]?.[enforcedLocale]?.trim() ?? '';
      if (value.length === 0) {
        out.push({ group, slotId: slot.id, slotName: slot.name });
      }
    }
  }
  return out;
}

// =============================================================================
// Step 1: Preview
// =============================================================================

function StepPreview({
  state,
  dispatch,
  isLoadingDocs,
  removesAiUserPrompt,
}: {
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
  isLoadingDocs: boolean;
  removesAiUserPrompt: boolean;
}) {
  const t = useI18n();
  return (
    <div style={{ padding: '20px', overflow: 'auto', flex: 1 }}>
      <div className={styles.detailSectionTitle}>
        {t['com.streamlain.marketplace.publish.scope.contents']()}
      </div>

      {isLoadingDocs && (
        <div className={styles.stateContainer}>
          <div className={styles.spinner} />
          {t['com.streamlain.marketplace.publish.scope.loading']()}
        </div>
      )}

      {!isLoadingDocs && state.dependentDocs.length === 0 && (
        <div className={styles.stateContainer}>
          {t['com.streamlain.marketplace.publish.scope.no-dependencies']()}
        </div>
      )}

      {!isLoadingDocs && state.dependentDocs.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          {state.dependentDocs.map(doc => (
            <div key={doc.id} className={styles.credentialItem}>
              <span>{doc.title}</span>
              <span className={styles.cardCategoryBadge}>{doc.type}</span>
              <span className={styles.cardCategoryBadge}>
                {t['com.streamlain.marketplace.publish.scope.exported']()}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Warnings */}
      <div className={styles.credentialWarning}>
        {'\u26A0'}{' '}
        {t['com.streamlain.marketplace.publish.scope.no-table-data']()}
      </div>
      <div className={styles.credentialWarning}>
        {'\u26A0'}{' '}
        {t['com.streamlain.marketplace.publish.scope.no-runtime-data']()}
      </div>
      {removesAiUserPrompt && (
        <div className={styles.credentialWarning}>
          {'\u26A0'}{' '}
          {t['com.streamlain.marketplace.publish.scope.no-user-prompt']()}
        </div>
      )}

      {/* Acknowledgement checkbox */}
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginTop: '16px',
          fontSize: '13px',
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={state.previewAcknowledged}
          onChange={e =>
            dispatch({ type: 'SET_PREVIEW_ACK', value: e.target.checked })
          }
        />
        {t['com.streamlain.marketplace.publish.scope.confirm']()}
      </label>
    </div>
  );
}

// =============================================================================
// Step 2: Metadata
// =============================================================================

function StepMetadata({
  state,
  dispatch,
  workspaceId,
  input,
}: {
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
  workspaceId?: string;
  input: PublishWizardInput;
}) {
  const t = useI18n();
  const picker = useLocalePicker();
  const [tagInput, setTagInput] = useState('');
  const existingTemplateId = input.existingTemplateId;
  // sl-k94y.7.2: visibility-aware locale baseline. Backend's
  // MarketplaceValidationService enforces 'en' for GLOBAL/SYSTEM and the
  // deployment defaultLocale for WORKSPACE/TENANT — wizard mirrors that
  // policy verbatim so submit cannot 422 on a `requiredLocales` mismatch.
  const enforcedLocale = picker
    ? resolvePublishEnforcedLocale(
        state.visibility,
        picker.settings.defaultLocale
      )
    : 'en';

  const handleAddTag = useCallback(
    (value: string) => {
      const tag = value.trim();
      if (!tag || state.metadata.tags.length >= 5) return;
      if (state.metadata.tags.includes(tag)) return;
      dispatch({
        type: 'SET_METADATA',
        metadata: { tags: [...state.metadata.tags, tag] },
      });
    },
    [state.metadata.tags, dispatch]
  );

  const handleRemoveTag = useCallback(
    (tag: string) => {
      dispatch({
        type: 'SET_METADATA',
        metadata: {
          tags: state.metadata.tags.filter(t => t !== tag),
        },
      });
    },
    [state.metadata.tags, dispatch]
  );

  const handleTagKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        handleAddTag(tagInput);
        setTagInput('');
      }
    },
    [tagInput, handleAddTag]
  );

  return (
    <div
      style={{
        padding: '20px',
        overflow: 'auto',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      {/* Preview Image — AI Generate or Manual Upload */}
      <PublishPreviewImageEditor
        workspaceId={workspaceId}
        blobId={state.metadata.previewImageBlobId}
        metadata={state.metadata}
        onUploaded={blobId =>
          dispatch({
            type: 'SET_METADATA',
            metadata: { previewImageBlobId: blobId },
          })
        }
      />

      {/* Title — sl-k94y.7.2: per-locale tabs via I18nFieldEditor.
          enforcedLocale is required (GLOBAL → 'en', WORKSPACE →
          deployment defaultLocale) and the wizard re-derives the
          policy whenever visibility changes. The anchor id matches the
          backend MarketplaceLocaleRequired.fieldPath[0] so submit-error
          handling can scrollIntoView. */}
      {picker && (
        <div id="publish-field-titlesI18n">
          <I18nFieldEditor
            value={state.metadata.titlesI18n}
            onChange={next =>
              dispatch({
                type: 'SET_METADATA',
                metadata: { titlesI18n: next },
              })
            }
            locales={picker.settings.availableLocales}
            defaultLocale={picker.settings.defaultLocale}
            label={t['com.streamlain.marketplace.publish.details.title']()}
            placeholder={t[
              'com.streamlain.marketplace.publish.details.title-placeholder'
            ]()}
            requiredLocales={[enforcedLocale]}
            onTranslate={
              workspaceId
                ? createWizardFieldTranslate(workspaceId, 'title')
                : undefined
            }
          />
        </div>
      )}

      {/* Description */}
      <div id="publish-field-descriptionsI18n">
        <PublishDescriptionEditor
          workspaceId={workspaceId}
          entityType={state.metadata.templateType}
          entityRef={
            input.kind === 'doc'
              ? { source: 'doc', docId: input.docId }
              : input.kind === 'app'
                ? { source: 'app', rootDocId: input.rootDocId }
                : input.kind === 'app_block'
                  ? {
                      source: 'app_block',
                      rootDocId: input.rootDocId,
                      customAppId: input.customAppId,
                      appBlockId: input.appBlockId,
                    }
                  : input.kind === 'automation'
                    ? {
                        source: 'automation',
                        automationBlockId: input.automationBlockId,
                      }
                    : input.kind === 'workflow'
                      ? { source: 'workflow', workflowId: input.workflowId }
                      : {
                          source: 'step',
                          workflowId: input.workflowId,
                          stepId: input.stepId,
                        }
          }
          title={picker?.pickTitle(state.metadata.titlesI18n) ?? ''}
          category={state.metadata.category}
          summary={t[
            'com.streamlain.marketplace.publish.details.description-hint'
          ]()}
          fieldHints={state.dependentDocs.map(
            doc => `${doc.type}: ${doc.title}`
          )}
          values={
            state.metadata.descriptionsI18n ?? {
              de: state.metadata.description,
            }
          }
          onChange={next =>
            dispatch({
              type: 'SET_METADATA',
              metadata: {
                description: getPrimaryPublishDescription(next, enforcedLocale),
                descriptionsI18n: next,
              },
            })
          }
          enforcedLocale={enforcedLocale}
        />
      </div>

      {/* Category */}
      <div>
        <div className={styles.detailSectionTitle}>
          {t['com.streamlain.marketplace.publish.details.category']()} *
        </div>
        <CustomSelect
          options={CATEGORIES.map(cat => ({ value: cat, label: cat }))}
          value={state.metadata.category}
          onChange={v =>
            dispatch({ type: 'SET_METADATA', metadata: { category: v } })
          }
          placeholder={t[
            'com.streamlain.marketplace.publish.details.category-placeholder'
          ]()}
          className={styles.customSelectFullWidth}
        />
      </div>

      {/* Tags */}
      <div>
        <div className={styles.detailSectionTitle}>
          {t['com.streamlain.marketplace.publish.details.tags']()}
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            marginBottom: '8px',
          }}
        >
          {state.metadata.tags.map(tag => (
            <span
              key={tag}
              className={styles.cardCategoryBadge}
              style={{ cursor: 'pointer' }}
              onClick={() => handleRemoveTag(tag)}
              title={t[
                'com.streamlain.marketplace.publish.details.remove-tag'
              ]()}
            >
              {tag} {'\u00D7'}
            </span>
          ))}
        </div>
        {state.metadata.tags.length < 5 && (
          <input
            className={styles.searchInput}
            type="text"
            placeholder={t[
              'com.streamlain.marketplace.publish.details.tag-placeholder'
            ]()}
            value={tagInput}
            onChange={e => setTagInput(e.target.value)}
            onKeyDown={handleTagKeyDown}
            style={{ width: '100%' }}
          />
        )}
      </div>

      {/* sl-mkx.4.1: Template kind is determined by the publish entry-point
          (discriminated-union input), no longer user-switchable mid-wizard. */}

      {/* sl-mkx.4.2: Step-specific slot documentation. The managed step type
          is derived from the selected workflow node at the entry point; users
          must not reclassify the source step while publishing it. */}
      {state.metadata.templateType === 'step' && (
        <>
          {/* sl-mkx.4.2 + sl-k94y.7.2: per-slot, per-locale description
              editor. Each slot delegates to I18nFieldEditor (same tab UI
              as Title/Description). The wizard threads the deployment
              locale policy through so the slot-editor doesn't need its
              own picker hook. sl-k94y Phase 4b R3 (Cluster B): the slot
              baseline is now universal, so the editor always marks
              required slots — no `baselineEnforced` gating prop. */}
          {input.kind === 'step' && picker && (
            <div>
              <div className={styles.detailSectionTitle}>
                {t['com.streamlain.marketplace.publish.details.slots']()}
              </div>
              <SlotDescriptionEditor
                workspaceId={workspaceId}
                inputSlots={input.inputSlots}
                configSlots={input.configSlots}
                outputSlots={input.outputSlots}
                availableLocales={picker.settings.availableLocales}
                defaultLocale={picker.settings.defaultLocale}
                enforcedLocale={enforcedLocale}
                descriptionsI18n={state.metadata.descriptionsI18n ?? {}}
                title={picker.pickTitle(state.metadata.titlesI18n) ?? ''}
                category={state.metadata.category}
                value={state.slotDescriptions}
                onChange={next =>
                  dispatch({ type: 'SET_SLOT_DESCRIPTIONS', value: next })
                }
              />
            </div>
          )}
        </>
      )}

      {/* sl-mkx.4.2: changelog is required when re-publishing an existing
          template. First publishes omit it. */}
      {existingTemplateId && (
        <div>
          <div className={styles.detailSectionTitle}>
            {t['com.streamlain.marketplace.publish.details.changelog']()} *
          </div>
          <textarea
            className={styles.searchInput}
            placeholder={t[
              'com.streamlain.marketplace.publish.details.changelog-placeholder'
            ]()}
            value={state.changelog}
            onChange={e =>
              dispatch({ type: 'SET_CHANGELOG', value: e.target.value })
            }
            style={{ width: '100%', minHeight: 80, resize: 'vertical' }}
          />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Step 3: Credential Review
// =============================================================================

function StepCredentialReview({
  state,
  dispatch,
  isLoadingCredentials,
}: {
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
  isLoadingCredentials: boolean;
}) {
  const t = useI18n();
  const externalCredentials = state.credentials.filter(c => !c.isAiGateway);
  const aiGatewayCredentials = state.credentials.filter(c => c.isAiGateway);

  return (
    <div style={{ padding: '20px', overflow: 'auto', flex: 1 }}>
      <div className={styles.detailSectionTitle}>
        {t['com.streamlain.marketplace.publish.safety.credentials']()}
      </div>

      {isLoadingCredentials && (
        <div className={styles.stateContainer}>
          <div className={styles.spinner} />
          {t['com.streamlain.marketplace.publish.safety.analyzing']()}
        </div>
      )}

      {!isLoadingCredentials && state.credentials.length === 0 && (
        <div className={styles.installSuccess}>
          <span className={styles.successCheckmark}>{'\u2713'}</span>
          {t['com.streamlain.marketplace.publish.safety.no-credentials']()}
        </div>
      )}

      {/* AI Gateway Credentials */}
      {aiGatewayCredentials.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div
            style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}
          >
            {t['com.streamlain.marketplace.publish.safety.ai-gateway']()}
          </div>
          {aiGatewayCredentials.map(cred => (
            <div key={cred.id} className={styles.credentialItem}>
              <span>{cred.displayName}</span>
              <span className={styles.cardCategoryBadge}>{cred.type}</span>
              <span
                className={styles.cardCategoryBadge}
                style={{
                  background: `color-mix(in srgb, var(--streamlain-success-color, #2E7D32) 12%, transparent)`,
                  color: 'var(--streamlain-success-color, #2E7D32)',
                }}
              >
                {t['com.streamlain.marketplace.publish.safety.provided']()}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* External Credentials */}
      {externalCredentials.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div
            style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px' }}
          >
            {t['com.streamlain.marketplace.publish.safety.external']()}
          </div>
          {externalCredentials.map(cred => (
            <div key={cred.id} className={styles.credentialItem}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={cred.isRequired}
                  onChange={e =>
                    dispatch({
                      type: 'UPDATE_CREDENTIAL',
                      id: cred.id,
                      changes: { isRequired: e.target.checked },
                    })
                  }
                />
                <span className={styles.cardCategoryBadge}>{cred.type}</span>
              </label>
              <input
                className={styles.searchInput}
                type="text"
                value={cred.displayName}
                onChange={e =>
                  dispatch({
                    type: 'UPDATE_CREDENTIAL',
                    id: cred.id,
                    changes: { displayName: e.target.value },
                  })
                }
                style={{ flex: 1, marginLeft: '8px' }}
                placeholder={t[
                  'com.streamlain.marketplace.publish.safety.credential-name'
                ]()}
              />
            </div>
          ))}
        </div>
      )}

      {!isLoadingCredentials && externalCredentials.length > 0 && (
        <div className={styles.credentialWarning}>
          {'\u26A0'}{' '}
          {t['com.streamlain.marketplace.publish.safety.external-hint']()}
        </div>
      )}
      <div className={styles.installSummary}>
        {t['com.streamlain.marketplace.publish.safety.automatic']()}
      </div>
    </div>
  );
}

// Visibility is edited with scope in Step 1.

function StepVisibility({
  state,
  dispatch,
}: {
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
}) {
  return (
    <div style={{ padding: '20px', flex: 1 }}>
      <PublishVisibilitySelector
        value={state.visibility}
        onChange={value => dispatch({ type: 'SET_VISIBILITY', value })}
      />
    </div>
  );
}

function isGlobalRightsComplete(state: WizardState): boolean {
  if (state.visibility !== 'global') return true;
  const rights = state.rightsMetadata;
  const externalOrLicensed =
    rights.provenance === 'licensed_partner' ||
    rights.provenance === 'external_reference_only' ||
    rights.containsThirdPartyContent ||
    rights.createdFromExternalTemplate;
  const sourceRequired =
    rights.provenance === 'external_reference_only' ||
    rights.createdFromExternalTemplate;
  return (
    rights.rightsConfirmed &&
    rights.author.trim().length > 0 &&
    rights.licenseType.trim().length > 0 &&
    (!externalOrLicensed ||
      (rights.permissionEvidence ?? '').trim().length > 0) &&
    (!sourceRequired || (rights.sourceUrl ?? '').trim().length > 0)
  );
}

function TemplateRightsMetadataForm({
  state,
  dispatch,
}: {
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
}) {
  const t = useI18n();
  if (state.visibility !== 'global') return null;
  const rights = state.rightsMetadata;
  const update = (value: Partial<TemplateRightsMetadata>) =>
    dispatch({ type: 'SET_RIGHTS_METADATA', value });

  return (
    <div
      className={styles.installSummary}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <div className={styles.detailSectionTitle}>
        {t['com.streamlain.marketplace.publish.rights.title']()}
      </div>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span>
          {t['com.streamlain.marketplace.publish.rights.provenance']()}
        </span>
        <select
          className={styles.searchInput}
          value={rights.provenance}
          onChange={e =>
            update({
              provenance: e.target
                .value as TemplateRightsMetadata['provenance'],
            })
          }
        >
          <option value="user_generated">
            {t['com.streamlain.marketplace.publish.rights.provenance.user']()}
          </option>
          <option value="first_party">
            {t[
              'com.streamlain.marketplace.publish.rights.provenance.first-party'
            ]()}
          </option>
          <option value="licensed_partner">
            {t[
              'com.streamlain.marketplace.publish.rights.provenance.partner'
            ]()}
          </option>
          <option value="external_reference_only">
            {t[
              'com.streamlain.marketplace.publish.rights.provenance.external'
            ]()}
          </option>
        </select>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span>{t['com.streamlain.marketplace.publish.rights.author']()}</span>
        <input
          className={styles.searchInput}
          value={rights.author}
          onChange={e => update({ author: e.target.value })}
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span>{t['com.streamlain.marketplace.publish.rights.license']()}</span>
        <input
          className={styles.searchInput}
          value={rights.licenseType}
          onChange={e => update({ licenseType: e.target.value })}
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span>{t['com.streamlain.marketplace.publish.rights.source']()}</span>
        <input
          className={styles.searchInput}
          value={rights.sourceUrl ?? ''}
          onChange={e => update({ sourceUrl: e.target.value })}
        />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span>{t['com.streamlain.marketplace.publish.rights.evidence']()}</span>
        <textarea
          className={styles.searchInput}
          value={rights.permissionEvidence ?? ''}
          onChange={e => update({ permissionEvidence: e.target.value })}
          rows={3}
        />
      </label>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <input
          type="checkbox"
          checked={rights.containsThirdPartyContent}
          onChange={e =>
            update({ containsThirdPartyContent: e.target.checked })
          }
        />
        <span>
          {t['com.streamlain.marketplace.publish.rights.third-party']()}
        </span>
      </label>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <input
          type="checkbox"
          checked={rights.createdFromExternalTemplate}
          onChange={e =>
            update({ createdFromExternalTemplate: e.target.checked })
          }
        />
        <span>
          {t['com.streamlain.marketplace.publish.rights.external-template']()}
        </span>
      </label>
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <input
          type="checkbox"
          checked={rights.rightsConfirmed}
          onChange={e => update({ rightsConfirmed: e.target.checked })}
        />
        <span>{t['com.streamlain.marketplace.publish.rights.confirm']()}</span>
      </label>
    </div>
  );
}

function StepConsentSubmit({
  state,
  dispatch,
  missingSlotBaselines,
  onJumpToAnchor,
  removesAiUserPrompt,
  supportsTechnicalRevision,
  onSaveTechnicalRevision,
}: {
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
  missingSlotBaselines: ReadonlyArray<{
    group: 'inputSlots' | 'configSlots' | 'outputSlots';
    slotId: string;
    slotName: string;
  }>;
  onJumpToAnchor: (anchor: string) => void;
  removesAiUserPrompt: boolean;
  supportsTechnicalRevision: boolean;
  onSaveTechnicalRevision: () => Promise<void>;
}) {
  const t = useI18n();
  const picker = useLocalePicker();
  // Show publish result
  if (state.publishResult) {
    const isPendingReview = state.publishResult.status === 'PENDING_REVIEW';
    return (
      <div style={{ padding: '20px', flex: 1 }}>
        <div className={styles.installSuccess}>
          <span className={styles.successCheckmark}>{'\u2713'}</span>
          {isPendingReview
            ? t['com.streamlain.marketplace.publish.result.pending']()
            : t['com.streamlain.marketplace.publish.result.available']()}
        </div>
        <div className={styles.installSummary} style={{ marginTop: '12px' }}>
          {t['com.streamlain.marketplace.publish.result.summary']({
            title: picker?.pickTitle(state.metadata.titlesI18n) ?? '',
            version: state.publishResult.semanticVersion,
          })}
        </div>
      </div>
    );
  }

  // Show error with findings
  if (state.submitError) {
    return (
      <div style={{ padding: '20px', flex: 1 }}>
        <div
          className={styles.credentialWarning}
          style={{ marginBottom: '12px' }}
        >
          {'\u26A0'}{' '}
          {t['com.streamlain.marketplace.publish.error.failed']({
            message: state.submitError,
          })}
        </div>
        {state.submitFindings.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div className={styles.detailSectionTitle}>
              {t['com.streamlain.marketplace.publish.error.issues']()}
            </div>
            {state.submitFindings.map(
              (f: { step: string; reason: string }, i: number) => (
                <div key={i} className={styles.credentialItem}>
                  <span style={{ fontWeight: 600 }}>{f.step}</span>
                  <span>{f.reason}</span>
                </div>
              )
            )}
          </div>
        )}
        <button
          type="button"
          className={styles.cancelButton}
          onClick={() => {
            dispatch({ type: 'SET_SUBMIT_ERROR', error: null });
            dispatch({ type: 'SET_PUBLISH_STEP', step: null });
          }}
        >
          {t['com.streamlain.marketplace.publish.action.retry']()}
        </button>
      </div>
    );
  }

  // Show progress
  if (state.isSubmitting && state.publishStep) {
    const steps: Array<{ key: PublishStep; label: string }> = [
      {
        key: 'packaging',
        label: t['com.streamlain.marketplace.publish.progress.packaging'](),
      },
      {
        key: 'analyzing',
        label: t['com.streamlain.marketplace.publish.progress.analyzing'](),
      },
      {
        key: 'uploading',
        label: t['com.streamlain.marketplace.publish.progress.uploading'](),
      },
    ];
    return (
      <div style={{ padding: '20px', flex: 1 }}>
        <div className={styles.detailSectionTitle}>
          {t['com.streamlain.marketplace.publish.progress.title']()}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            marginTop: '12px',
          }}
        >
          {steps.map(s => {
            const stepOrder = ['packaging', 'analyzing', 'uploading', 'done'];
            const currentIdx = stepOrder.indexOf(state.publishStep!);
            const stepIdx = stepOrder.indexOf(s.key);
            const isDone = stepIdx < currentIdx;
            const isActive = stepIdx === currentIdx;
            return (
              <div
                key={s.key}
                className={clsx(
                  styles.installStepItem,
                  isDone && styles.installStepDone,
                  isActive && styles.installStepActive
                )}
              >
                {isDone ? '\u2713 ' : isActive ? '\u25CF ' : '\u25CB '}
                {s.label}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // sl-k94y.7.2: visibility-aware locale baseline gate. The user can change
  // visibility after filling details, so we re-derive the
  // enforced locale here and surface a banner with a one-click jump back to
  // Metadata if the baseline isn't satisfied. Mirrors the backend
  // MarketplaceLocaleRequired error so submit can never 422 on metadata.
  const enforcedLocale = picker
    ? resolvePublishEnforcedLocale(
        state.visibility,
        picker.settings.defaultLocale
      )
    : null;
  const titleMissing =
    !!enforcedLocale &&
    (state.metadata.titlesI18n[enforcedLocale] ?? '').trim().length === 0;
  const descriptionShort =
    !!enforcedLocale &&
    !isPublishDescriptionComplete(
      state.metadata.descriptionsI18n ?? {},
      enforcedLocale
    );
  const slotsMissing = missingSlotBaselines.length > 0;
  const baselineSatisfied = !titleMissing && !descriptionShort && !slotsMissing;

  // Show consent form
  return (
    <div
      style={{
        padding: '20px',
        overflow: 'auto',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      {enforcedLocale && !baselineSatisfied && (
        <div className={styles.credentialWarning}>
          <div style={{ marginBottom: '8px' }}>
            {'⚠'}{' '}
            {t['com.streamlain.marketplace.publish.release.locale-required']({
              locale: enforcedLocale.toUpperCase(),
            })}
          </div>
          <div style={{ fontSize: '12px', marginBottom: '8px' }}>
            {titleMissing && (
              <div>
                {t['com.streamlain.marketplace.publish.release.title-required'](
                  { locale: enforcedLocale.toUpperCase() }
                )}
              </div>
            )}
            {descriptionShort && (
              <div>
                {t[
                  'com.streamlain.marketplace.publish.release.description-required'
                ]({
                  count: String(MIN_PUBLISH_DESCRIPTION_LENGTH),
                  locale: enforcedLocale.toUpperCase(),
                })}
              </div>
            )}
            {slotsMissing &&
              missingSlotBaselines.map(slot => (
                <div
                  key={slot.slotId}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                    marginTop: 4,
                  }}
                >
                  <span>
                    {t[
                      'com.streamlain.marketplace.publish.release.slot-required'
                    ]({
                      slot: slot.slotName,
                      locale: enforcedLocale.toUpperCase(),
                    })}
                  </span>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => {
                      dispatch({ type: 'SET_STEP', step: 2 });
                      onJumpToAnchor(`publish-slot-${slot.slotId}`);
                    }}
                  >
                    {t[
                      'com.streamlain.marketplace.publish.release.open-slot'
                    ]()}
                  </button>
                </div>
              ))}
          </div>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={() => dispatch({ type: 'SET_STEP', step: 2 })}
          >
            {t['com.streamlain.marketplace.publish.release.back-details']()}
          </button>
        </div>
      )}

      {removesAiUserPrompt && (
        <div className={styles.credentialWarning}>
          {'\u26A0'}{' '}
          {t['com.streamlain.marketplace.publish.scope.no-user-prompt']()}
        </div>
      )}

      {supportsTechnicalRevision ? (
        <section className={styles.credentialWarning} aria-live="polite">
          <strong>
            {t['com.streamlain.marketplace.publish.technical.title']()}
          </strong>
          <p style={{ fontSize: 12, margin: '6px 0' }}>
            {t['com.streamlain.marketplace.publish.technical.description']()}
          </p>
          {state.technicalRevision ? (
            <div style={{ fontSize: 12, marginBottom: 8 }}>
              <div>
                {t['com.streamlain.marketplace.publish.technical.summary']({
                  count: String(state.technicalRevision.automationCount),
                  maximum: String(state.technicalRevision.maxAutomations),
                  disposition: state.technicalRevision.reused
                    ? t['com.streamlain.marketplace.publish.technical.reused']()
                    : t['com.streamlain.marketplace.publish.technical.saved'](),
                  revision:
                    state.technicalRevision.rootArtifactRevisionId.slice(0, 12),
                })}
              </div>
              {state.technicalRevision.revisions.length > 0 ? (
                <ul
                  aria-label={t[
                    'com.streamlain.marketplace.publish.technical.automations'
                  ]()}
                >
                  {state.technicalRevision.revisions.map(revision => (
                    <li key={revision.slotKey}>
                      {revision.label} · {revision.disposition} ·{' '}
                      {revision.inputs.join(', ') || '—'} →{' '}
                      {revision.outputs.join(', ') || '—'}
                    </li>
                  ))}
                </ul>
              ) : null}
              {state.technicalRevision.credentialRequirements.length > 0 ? (
                <div>
                  {t[
                    'com.streamlain.marketplace.publish.technical.credentials'
                  ]()}{' '}
                  {state.technicalRevision.credentialRequirements.join(', ')}
                </div>
              ) : null}
              {state.technicalRevision.endpoints.length > 0 ? (
                <div>
                  {t[
                    'com.streamlain.marketplace.publish.technical.endpoints'
                  ]()}{' '}
                  {state.technicalRevision.endpoints
                    .map(
                      endpoint => `${endpoint.url} (${endpoint.classification})`
                    )
                    .join(', ')}
                </div>
              ) : null}
              {state.technicalRevision.sharedResources.length > 0 ? (
                <div>
                  {t[
                    'com.streamlain.marketplace.publish.technical.shared-resources'
                  ]()}{' '}
                  {state.technicalRevision.sharedResources.join(', ')}
                </div>
              ) : null}
              {state.technicalRevision.findings.map(finding => (
                <div key={`${finding.severity}:${finding.message}`}>
                  {finding.message}
                </div>
              ))}
              {!state.technicalRevision.canPromoteListing ? (
                <div>
                  {t[
                    'com.streamlain.marketplace.publish.technical.promotion-blocked'
                  ]()}
                </div>
              ) : null}
            </div>
          ) : null}
          {state.technicalRevisionError ? (
            <div style={{ fontSize: 12, marginBottom: 8 }}>
              {state.technicalRevisionError}
            </div>
          ) : null}
          <button
            type="button"
            className={styles.cancelButton}
            disabled={
              state.isSavingTechnicalRevision ||
              state.technicalRevision?.canSaveTechnicalRevision === false
            }
            onClick={() => void onSaveTechnicalRevision()}
          >
            {state.isSavingTechnicalRevision
              ? t[
                  'com.streamlain.marketplace.publish.technical.action.saving'
                ]()
              : state.technicalRevision
                ? t[
                    'com.streamlain.marketplace.publish.technical.action.refresh'
                  ]()
                : t[
                    'com.streamlain.marketplace.publish.technical.action.save'
                  ]()}
          </button>
        </section>
      ) : null}

      <PublishConsentSection
        title={picker?.pickTitle(state.metadata.titlesI18n) ?? ''}
        category={state.metadata.category}
        extraSummary={
          state.credentials.filter(c => c.isRequired).length > 0
            ? t['com.streamlain.marketplace.publish.release.credential-count']({
                count: String(
                  state.credentials.filter(c => c.isRequired).length
                ),
              })
            : undefined
        }
        consentGdpr={state.consentGdpr}
        consentLicense={state.consentLicense}
        onConsentGdprChange={value =>
          dispatch({ type: 'SET_CONSENT_GDPR', value })
        }
        onConsentLicenseChange={value =>
          dispatch({ type: 'SET_CONSENT_LICENSE', value })
        }
      />

      <TemplateRightsMetadataForm state={state} dispatch={dispatch} />
    </div>
  );
}

// =============================================================================
// PublishWizard
// =============================================================================

/**
 * sl-mkx.4.1: Discriminated-union input for the publish wizard. The kind
 * decides which template-type the wizard produces; the entry-point passes
 * the right kind instead of asking the user to pick one via radio mid-flow.
 *
 * - step: a single node from a workflow, published as a reusable step
 * - workflow: the whole SWR workflow snapshot
 * - automation: the automation-block Y.js doc + its dependencies
 */
export type PublishWizardInput = PublishWizardSource;

export interface PublishWizardProps {
  open: boolean;
  onClose: () => void;
  input: PublishWizardInput;
}

export function PublishWizard({ open, onClose, input }: PublishWizardProps) {
  const t = useI18n();
  const workspaceId: string = input.workspaceId;
  const existingTemplateId: string | undefined = input.existingTemplateId;
  const automationBlockTitle: string | undefined = input.title;
  const sourceId = getTemplatePublishSourceId(input);
  const analysisSourceId =
    input.kind === 'workflow' || input.kind === 'step'
      ? input.workflowId
      : input.kind === 'automation'
        ? input.automationBlockId
        : undefined;
  const reviewTemplateType =
    input.kind === 'doc' || input.kind === 'app' || input.kind === 'app_block'
      ? null
      : input.kind;
  const { isFeatureAllowed } = useFeatureGate();
  const publishAllowed = isFeatureAllowed('marketplace-publish');
  // sl-k94y.7.2: locale picker drives visibility-aware validation; one
  // hook call at the wizard root keeps the policy single-sourced.
  const picker = useLocalePicker();

  const [state, dispatch] = useReducer(wizardReducer, {
    step: 1,
    docIds: [],
    dependentDocs: [],
    metadata: {
      titlesI18n: {},
      description: '',
      descriptionsI18n: {},
      category: '',
      tags: [],
      // sl-mkx.4.1: template-type is authoritative from the entry-point.
      templateType: input.kind,
      // sl-mkx.4.2: step entry-points carry the managed-step kind.
      stepInsertType: input.kind === 'step' ? input.stepInsertType : undefined,
    },
    credentials: [],
    previewAcknowledged: false,
    visibility: input.initialVisibility ?? 'workspace',
    // sl-mkx.4.2: step-kind seeds pre-authored slot descriptions; the other
    // kinds keep the map empty and never render the editor.
    slotDescriptions:
      input.kind === 'step' ? (input.initialSlotDescriptions ?? {}) : {},
    changelog: '',
    consentGdpr: false,
    consentLicense: false,
    rightsMetadata: {
      provenance: 'user_generated',
      licenseType: 'streamlain_marketplace_install_use',
      author: '',
      containsThirdPartyContent: false,
      createdFromExternalTemplate: false,
      rightsConfirmed: false,
    },
    isSubmitting: false,
    publishStep: null,
    submitError: null,
    submitFindings: [],
    publishResult: null,
    technicalRevision: null,
    isSavingTechnicalRevision: false,
    technicalRevisionError: null,
  });

  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [isLoadingCredentials, setIsLoadingCredentials] = useState(false);

  // sl-publish-preconfig.L3: Parameterized values merged with publisher
  // overrides; emitted by PrePublishReview as the author edits them.
  // Sent verbatim as `parameterizedValueOverrides` in the publish body so
  // the backend merges them onto the sanitizer output before persist.
  const [parameterizedValueOverrides, setParameterizedValueOverrides] =
    useState<ParameterizedValue[]>([]);

  // sl-mkx.5.2: Trigger overrides from the "Trigger Review" sub-panel
  // (kind='automation' only). Emitted verbatim as
  // `parameterizedTriggerOverrides` in the publish body so the sanitizer
  // replaces the matching dotted-path values with placeholder tokens.
  const [parameterizedTriggerOverrides, setParameterizedTriggerOverrides] =
    useState<ParameterizedTriggerOverride[]>([]);

  useEffect(() => {
    if (!picker || !automationBlockTitle) return;
    const enforcedLocale = resolvePublishEnforcedLocale(
      state.visibility,
      picker.settings.defaultLocale
    );
    const hasAnyTitle = Object.values(state.metadata.titlesI18n).some(
      value => typeof value === 'string' && value.trim().length > 0
    );
    if (hasAnyTitle) return;
    dispatch({
      type: 'SET_METADATA',
      metadata: { titlesI18n: { [enforcedLocale]: automationBlockTitle } },
    });
  }, [
    automationBlockTitle,
    picker,
    state.metadata.titlesI18n,
    state.visibility,
  ]);

  // Fetch dependent docs when wizard opens
  const fetchDependentDocs = useCallback(async () => {
    if (input.kind !== 'doc') {
      dispatch({ type: 'SET_DEPENDENT_DOCS', docs: [] });
      return;
    }
    dispatch({ type: 'SET_DEPENDENT_DOCS', docs: [] });
    setIsLoadingDocs(false);
  }, [input.kind]);

  // Fetch credentials when reaching step 3
  const fetchCredentials = useCallback(async () => {
    if (!workspaceId || !analysisSourceId) return;
    setIsLoadingCredentials(true);
    try {
      const res = await workspaceApiFetch(
        `/api/workspace/${workspaceId}/marketplace/analyze/${analysisSourceId}?templateType=${state.metadata.templateType}`,
        { credentials: 'include' }
      );
      if (res.ok) {
        dispatch({
          type: 'SET_CREDENTIALS',
          credentials: normalizeCredentialRequirements(await res.json()),
        });
      }
    } catch {
      // Silent fail — empty credential list is acceptable
    } finally {
      setIsLoadingCredentials(false);
    }
  }, [workspaceId, analysisSourceId, state.metadata.templateType]);

  useEffect(() => {
    if (open) {
      void fetchDependentDocs();
    }
  }, [open, fetchDependentDocs]);

  // Fetch credentials when entering step 3
  useEffect(() => {
    if (state.step === 3 && state.credentials.length === 0) {
      void fetchCredentials();
    }
  }, [state.step, state.credentials.length, fetchCredentials]);

  const handleSaveTechnicalRevision = useCallback(async () => {
    if (!workspaceId) return;
    const request = buildTechnicalRevisionRequest({
      workspaceId,
      source: input,
    });
    if (!request) return;
    dispatch({ type: 'SET_TECHNICAL_REVISION_SAVING', value: true });
    dispatch({ type: 'SET_TECHNICAL_REVISION_ERROR', error: null });
    try {
      const response = await workspaceApiFetch(request.url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request.body),
      });
      if (!response.ok) {
        const error = (await response.json().catch(() => null)) as {
          message?: string;
        } | null;
        throw new Error(error?.message ?? 'Technical revision save failed');
      }
      dispatch({
        type: 'SET_TECHNICAL_REVISION',
        result: (await response.json()) as NonNullable<
          WizardState['technicalRevision']
        >,
      });
    } catch (error) {
      dispatch({
        type: 'SET_TECHNICAL_REVISION_ERROR',
        error: error instanceof Error ? error.message : 'Save failed',
      });
    } finally {
      dispatch({ type: 'SET_TECHNICAL_REVISION_SAVING', value: false });
    }
  }, [input, workspaceId]);

  // Submit publish
  const handleSubmitPublish = useCallback(async () => {
    if (!workspaceId || !sourceId) return;
    dispatch({ type: 'SET_SUBMITTING', value: true });
    dispatch({ type: 'SET_SUBMIT_ERROR', error: null });

    try {
      // Step 1: Packaging
      dispatch({ type: 'SET_PUBLISH_STEP', step: 'packaging' });
      await new Promise(r => setTimeout(r, 500));

      // Step 2: Analyzing
      dispatch({ type: 'SET_PUBLISH_STEP', step: 'analyzing' });
      await new Promise(r => setTimeout(r, 300));

      // Step 3: Uploading
      dispatch({ type: 'SET_PUBLISH_STEP', step: 'uploading' });

      const { url, body } = buildPublishRequest({
        workspaceId,
        source: input,
        metadata: state.metadata,
        visibility: state.visibility,
        slotDescriptions: state.slotDescriptions,
        existingTemplateId,
        changelog: state.changelog,
        parameterizedValueOverrides,
        parameterizedTriggerOverrides,
        rightsMetadata: state.rightsMetadata,
        expectedRootArtifactRevisionId:
          state.technicalRevision?.rootArtifactRevisionId,
      });

      const res = await workspaceApiFetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = (await res
          .json()
          .catch(() => ({ message: 'Publication failed' }))) as {
          code?: string;
          message?: string;
          findings?: Array<{ step: string; reason: string }>;
          fieldPath?: ReadonlyArray<string>;
          requiredLocale?: string;
        };
        // sl-k94y.7.2: Backend `MarketplaceLocaleRequired` (422) carries
        // `fieldPath`. Resolve to a wizard-side anchor and queue the
        // scroll until Step 2 commits — `dispatch` and `scrollIntoView`
        // cannot run in the same task. `pendingScrollAnchor` effect
        // owns the timing.
        if (
          err.code === 'MarketplaceLocaleRequired' &&
          err.fieldPath &&
          err.fieldPath.length > 0
        ) {
          dispatch({ type: 'SET_STEP', step: 2 });
          const anchor = resolvePublishFieldAnchor(err.fieldPath);
          if (anchor) setPendingScrollAnchor(anchor);
        }
        dispatch({
          type: 'SET_SUBMIT_ERROR',
          error: err.message ?? 'Publication failed',
          findings: err.findings,
        });
        dispatch({ type: 'SET_PUBLISH_STEP', step: 'error' });
        return;
      }

      const result: PublishResult = await res.json();
      dispatch({ type: 'SET_PUBLISH_RESULT', result });
      dispatch({ type: 'SET_PUBLISH_STEP', step: 'done' });
    } catch {
      dispatch({
        type: 'SET_SUBMIT_ERROR',
        error: 'Network error — please try again',
      });
      dispatch({ type: 'SET_PUBLISH_STEP', step: 'error' });
    } finally {
      dispatch({ type: 'SET_SUBMITTING', value: false });
    }
  }, [
    workspaceId,
    sourceId,
    existingTemplateId,
    input,
    state.metadata,
    state.visibility,
    state.slotDescriptions,
    state.changelog,
    state.rightsMetadata,
    state.technicalRevision?.rootArtifactRevisionId,
    parameterizedValueOverrides,
    parameterizedTriggerOverrides,
  ]);

  // sl-kypm.5.2: Review step state (controlled by PrePublishReview component)
  const [reviewCanProceed, setReviewCanProceed] = useState(false);

  // sl-k94y.7.2: visibility-aware locale baseline mirrors backend
  // MarketplaceValidationService — `en` for GLOBAL, defaultLocale for
  // WORKSPACE. Picker may still be loading; gate validation on the picker
  // being ready so the wizard cannot proceed past the metadata step before
  // the policy is known.
  // sl-k94y Phase 4b R3 (Cluster B): the slot-baseline gate fires for
  // every visibility (universal), so the only remaining gate is whether
  // the picker has loaded.
  const wizardEnforcedLocale = picker
    ? resolvePublishEnforcedLocale(
        state.visibility,
        picker.settings.defaultLocale
      )
    : null;
  // sl-k94y Phase 4b R3 (Cluster B): collect missing required-slot
  // locales for any visibility — backend's `validatePublishedSlots` no
  // longer early-returns for deployment-bound publishes. Step 2 + Step 4
  // canProceed and the Step 4 banner all consume this list so submit
  // can never 422 on a missing slot baseline.
  const missingSlotBaselines: ReadonlyArray<{
    group: 'inputSlots' | 'configSlots' | 'outputSlots';
    slotId: string;
    slotName: string;
  }> =
    wizardEnforcedLocale && input.kind === 'step'
      ? collectMissingSlotBaselines(
          input.inputSlots,
          input.configSlots,
          input.outputSlots,
          state.slotDescriptions,
          wizardEnforcedLocale
        )
      : [];

  // sl-k94y.7.2: deferred scroll target for backend MarketplaceLocaleRequired
  // navigation. `dispatch({ step: 2 })` and `scrollIntoView(...)` cannot
  // run in the same task — React commits Step 2's DOM after the dispatch.
  // Effect waits for the matching step to render, then scrolls and clears.
  const [pendingScrollAnchor, setPendingScrollAnchor] = useState<string | null>(
    null
  );
  useEffect(() => {
    if (!pendingScrollAnchor) return;
    if (state.step !== 2) return;
    const el = document.getElementById(pendingScrollAnchor);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setPendingScrollAnchor(null);
  }, [pendingScrollAnchor, state.step]);

  // Step validation
  const canProceed = (() => {
    switch (state.step) {
      case 1:
        return state.previewAcknowledged;
      case 2: {
        if (!wizardEnforcedLocale) return false;
        return (
          (state.metadata.titlesI18n[wizardEnforcedLocale] ?? '').trim()
            .length > 0 &&
          // sl-k94y.7.2: enforce backend visibility-aware locale baseline at
          // Step 2 (Rule 7 — align at source). 50-char wizard quality bar
          // also targets the enforced locale.
          isPublishDescriptionComplete(
            state.metadata.descriptionsI18n ?? {},
            wizardEnforcedLocale
          ) &&
          state.metadata.category.length > 0 &&
          // sl-mkx.4.2: re-publishes require a non-empty changelog.
          (!existingTemplateId || state.changelog.trim().length > 0) &&
          // sl-k94y.7.2: required-slot baseline mirrors backend
          // validatePublishedSlots — only enforced for cross-deployment
          // visibility, no-op for WORKSPACE.
          missingSlotBaselines.length === 0
        );
      }
      case 3:
        // Credentials are informational. Workflow/automation findings remain
        // an explicit gate; simpler artifacts are checked by their backend
        // contract validators during publish.
        return input.kind === 'step' ||
          input.kind === 'doc' ||
          input.kind === 'app' ||
          input.kind === 'app_block'
          ? true
          : reviewCanProceed;
      case 4: {
        if (!wizardEnforcedLocale) return false;
        // sl-k94y.7.2: enforce visibility-aware metadata + slot baseline
        // at submit (defense-in-depth: Step 2 already gated this for the
        // visibility selected then, but the user can flip visibility at
        // Step 1).
        const titleOk =
          (state.metadata.titlesI18n[wizardEnforcedLocale] ?? '').trim()
            .length > 0;
        const descOk = isPublishDescriptionComplete(
          state.metadata.descriptionsI18n ?? {},
          wizardEnforcedLocale
        );
        const slotsOk = missingSlotBaselines.length === 0;
        const technicalPromotionReady =
          !['doc', 'app', 'app_block'].includes(input.kind) ||
          state.technicalRevision?.canPromoteListing === true;
        return (
          titleOk &&
          descOk &&
          slotsOk &&
          technicalPromotionReady &&
          isGlobalRightsComplete(state) &&
          state.consentGdpr &&
          state.consentLicense &&
          !state.isSubmitting
        );
      }
    }
  })();

  const handleNext = useCallback(() => {
    if (state.step < 4) {
      dispatch({
        type: 'SET_STEP',
        step: (state.step + 1) as 1 | 2 | 3 | 4,
      });
    }
  }, [state.step]);

  const handleBack = useCallback(() => {
    if (state.step > 1) {
      dispatch({
        type: 'SET_STEP',
        step: (state.step - 1) as 1 | 2 | 3 | 4,
      });
    }
  }, [state.step]);

  if (!publishAllowed) return null;

  const isComplete = state.publishResult !== null;
  const removesAiUserPrompt = isStandaloneAiNodePublish(input);

  return (
    <Modal
      width={800}
      height={600}
      contentOptions={{
        style: {
          maxHeight: '80vh',
          padding: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      open={open}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <div className={styles.dialogHeader}>
        <span className={styles.dialogTitle}>
          {existingTemplateId
            ? t['com.streamlain.marketplace.publish.title.version']()
            : t['com.streamlain.marketplace.publish.title.new']()}
        </span>
      </div>

      <div className={styles.publishReviewSurface}>
        <button
          type="button"
          className={clsx(styles.publishEdgeButton, styles.publishEdgePrevious)}
          onClick={handleBack}
          disabled={state.step === 1 || state.isSubmitting}
          aria-label={t['com.streamlain.marketplace.publish.action.back']()}
        >
          {'‹'}
        </button>
        <div className={styles.publishReviewContent}>
          {state.step === 1 && (
            <>
              <StepPreview
                state={state}
                dispatch={dispatch}
                isLoadingDocs={isLoadingDocs}
                removesAiUserPrompt={removesAiUserPrompt}
              />
              <StepVisibility state={state} dispatch={dispatch} />
            </>
          )}
          {state.step === 2 && (
            <StepMetadata
              state={state}
              dispatch={dispatch}
              workspaceId={workspaceId}
              input={input}
            />
          )}
          {state.step === 3 && (
            <div className={styles.publishSafetyStack}>
              <StepCredentialReview
                state={state}
                dispatch={dispatch}
                isLoadingCredentials={isLoadingCredentials}
              />
              {reviewTemplateType ? (
                <PrePublishReview
                  workspaceId={workspaceId}
                  automationBlockId={analysisSourceId}
                  templateType={reviewTemplateType}
                  onReviewStateChange={setReviewCanProceed}
                  onParameterizedValuesChange={setParameterizedValueOverrides}
                />
              ) : null}
              {input.kind === 'automation' && (
                <TriggerReviewStep
                  workspaceId={workspaceId}
                  automationBlockId={input.automationBlockId}
                  onOverridesChange={setParameterizedTriggerOverrides}
                />
              )}
            </div>
          )}
          {state.step === 4 && (
            <StepConsentSubmit
              state={state}
              dispatch={dispatch}
              missingSlotBaselines={missingSlotBaselines}
              onJumpToAnchor={setPendingScrollAnchor}
              removesAiUserPrompt={removesAiUserPrompt}
              supportsTechnicalRevision={
                buildTechnicalRevisionRequest({
                  workspaceId: workspaceId ?? '',
                  source: input,
                }) !== null
              }
              onSaveTechnicalRevision={handleSaveTechnicalRevision}
            />
          )}
        </div>
        <button
          type="button"
          className={clsx(styles.publishEdgeButton, styles.publishEdgeNext)}
          onClick={handleNext}
          disabled={state.step === 4 || !canProceed || state.isSubmitting}
          aria-label={t['com.streamlain.marketplace.publish.action.next']()}
        >
          {'›'}
        </button>
      </div>

      <div
        className={styles.publishProgress}
        aria-label={t['com.streamlain.marketplace.publish.progress']()}
      >
        {REVIEW_STEP_KEYS.map((key, index) => {
          const step = (index + 1) as PublishReviewStep;
          return (
            <button
              type="button"
              key={key}
              className={clsx(
                styles.publishProgressStep,
                step === state.step && styles.publishProgressStepActive,
                step < state.step && styles.publishProgressStepDone
              )}
              onClick={() => dispatch({ type: 'SET_STEP', step })}
              disabled={
                !canNavigateToPublishStep({
                  currentStep: state.step,
                  targetStep: step,
                  currentStepComplete: canProceed,
                  isSubmitting: state.isSubmitting,
                })
              }
            >
              <span>{step}</span>
              {t[key]()}
            </button>
          );
        })}
      </div>

      <div className={styles.pagination}>
        <button
          type="button"
          className={styles.cancelButton}
          onClick={
            isComplete ? onClose : state.step === 1 ? onClose : handleBack
          }
          disabled={state.isSubmitting}
        >
          {isComplete
            ? t['com.streamlain.marketplace.publish.action.close']()
            : state.step === 1
              ? t['com.streamlain.marketplace.publish.action.cancel']()
              : t['com.streamlain.marketplace.publish.action.back']()}
        </button>
        <span className={styles.paginationInfo}>
          {t['com.streamlain.marketplace.publish.step-count']({
            current: String(state.step),
            total: '4',
          })}
        </span>
        {!isComplete && (
          <button
            type="button"
            className={styles.confirmButton}
            disabled={!canProceed || state.isSubmitting}
            onClick={state.step === 4 ? handleSubmitPublish : handleNext}
          >
            {state.step === 4
              ? state.visibility === 'global'
                ? t['com.streamlain.marketplace.publish.action.submit-review']()
                : t[
                    'com.streamlain.marketplace.publish.action.publish-workspace'
                  ]()
              : t['com.streamlain.marketplace.publish.action.next']()}
          </button>
        )}
      </div>
    </Modal>
  );
}
